import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Anchor, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ContractPage() {
  const { bookingId } = useParams();
  const signatureRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [boat, setBoat] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [cgvContent, setCgvContent] = useState('');

  useEffect(() => {
    fetchBooking();
    fetchCGV();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const res = await fetch(`${API}/bookings/${bookingId}`);
      if (!res.ok) throw new Error('Réservation introuvable');
      const data = await res.json();
      setBooking(data.booking);
      setCustomer(data.customer);
      setBoat(data.boat);
      if (data.booking.contract_signed) setSigned(true);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const fetchCGV = async () => {
    try {
      const res = await fetch(`${API}/cgv`);
      if (res.ok) {
        const data = await res.json();
        setCgvContent(data.content || '');
      }
    } catch (e) {
      console.error('Failed to fetch CGV:', e);
    }
  };

  const formatDateFr = (dateStr) => {
    if (!dateStr) return '';
    const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
    const [y, m, d] = dateStr.split('-');
    return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
  };

  const handleSign = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez signer dans le cadre ci-dessus');
      return;
    }
    if (!accepted) {
      toast.error('Veuillez accepter les conditions générales');
      return;
    }
    setSigning(true);
    try {
      const signatureData = signatureRef.current.toDataURL();
      const res = await fetch(`${API}/bookings/${bookingId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_data: signatureData })
      });
      if (!res.ok) throw new Error();
      setSigned(true);
      toast.success('Contrat signé avec succès !');
    } catch {
      toast.error('Erreur lors de la signature');
    }
    setSigning(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#099dbb] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#0a57a4] mb-2">Réservation introuvable</h1>
          <p className="text-[#858275]">Ce lien n'est plus valide ou la réservation n'existe pas.</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0a57a4] mb-3">Contrat signé !</h1>
          <p className="text-[#858275] mb-6">Merci, votre contrat de location a été signé avec succès. Vous recevrez une confirmation par email.</p>
          <div className="bg-white rounded-lg border border-[#dec5ae]/30 p-4 text-left space-y-2">
            <p className="text-sm"><span className="text-[#858275]">Date :</span> <strong className="text-[#0a57a4]">{formatDateFr(booking.date)}</strong></p>
            <p className="text-sm"><span className="text-[#858275]">Créneau :</span> <strong className="text-[#0a57a4]">{booking.time_slot}</strong></p>
            <p className="text-sm text-[#858275] mt-3 pt-3 border-t border-[#dec5ae]/20">Présentez-vous 10 minutes avant l'heure de départ au ponton d'embarquement sous le phare vert.</p>
          </div>
        </div>
      </div>
    );
  }

  const ttc = booking.total_price / 100;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <div className="bg-[#0a57a4] text-white py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Anchor className="w-6 h-6" />
          <div>
            <h1 className="font-bold text-lg">A Bella Barca</h1>
            <p className="text-white/70 text-xs">Contrat de location</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Booking summary */}
        <div className="bg-white rounded-lg border border-[#dec5ae]/30 overflow-hidden">
          <div className="bg-[#0a57a4] px-4 py-3">
            <p className="text-white font-semibold text-sm">Votre réservation</p>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-[#858275]">Client</span><p className="font-semibold text-[#0a57a4]">{customer?.first_name} {customer?.last_name}</p></div>
              <div><span className="text-[#858275]">Date</span><p className="font-semibold text-[#0a57a4]">{formatDateFr(booking.date)}</p></div>
              <div><span className="text-[#858275]">Créneau</span><p className="font-semibold text-[#0a57a4]">{booking.time_slot}</p></div>
              <div><span className="text-[#858275]">Montant</span><p className="font-semibold text-[#0a57a4]">{ttc}€ TTC</p></div>
            </div>
          </div>
        </div>

        {/* Contract text */}
        <div className="bg-white rounded-lg border border-[#dec5ae]/30 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#dec5ae]/20">
            <FileText className="w-4 h-4 text-[#0a57a4]" />
            <p className="font-semibold text-sm text-[#0a57a4]">Conditions générales de location</p>
          </div>
          <div className="p-4 max-h-[300px] overflow-y-auto">
            <pre className="text-xs text-[#333] whitespace-pre-wrap font-sans leading-relaxed">{cgvContent}</pre>
          </div>
        </div>

        {/* Accept + Sign */}
        <div className="bg-white rounded-lg border border-[#dec5ae]/30 p-4 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#099dbb]"
              data-testid="accept-terms"
            />
            <span className="text-sm text-[#333]">
              J'ai lu et j'accepte les conditions générales de location et le règlement de sécurité. Je m'engage à respecter les consignes de navigation.
            </span>
          </label>

          <div>
            <p className="text-sm font-semibold text-[#0a57a4] mb-2">Votre signature :</p>
            <div className="border-2 border-dashed border-[#dec5ae] rounded-lg overflow-hidden bg-white" data-testid="contract-signature-pad">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'w-full',
                  style: { width: '100%', height: '180px' }
                }}
                backgroundColor="white"
              />
            </div>
            <button
              onClick={() => signatureRef.current?.clear()}
              className="text-xs text-[#858275] hover:text-[#099dbb] mt-1 transition-colors"
            >
              Effacer la signature
            </button>
          </div>

          <Button
            onClick={handleSign}
            disabled={signing || !accepted}
            className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white py-6 text-base font-semibold"
            data-testid="sign-contract-btn"
          >
            {signing ? 'Signature en cours...' : 'Signer le contrat'}
          </Button>
        </div>
      </div>
    </div>
  );
}
